import React from 'react'
import { BtnV2, Icon, Link, l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { TouchedContactLimits } from 'common/billing/constants/TouchedContactLimits'
import { getActiveContacts } from 'common/billing/entities/billingVersions/selectors'
import { getConnectedChannels } from 'common/core/selectors/getConnectedChannels'
import { analyticsService } from 'utils/services/analytics'

import { UniversalPaywallContextFCProps } from '../Paywall/variants/UniversalPaywall/types'

import { ActiveContactsText } from './ActiveContactsText'
import modalImageDefault from './assets/modal_img.png'
import modalImageStopped from './assets/modal_img_stopped.png'
import { ChannelsText } from './ChannelsText'
import { InfoSectionText } from './InfoSectionText'

import cm from './PostMigrationToNewBillingModal.module.css'

const ARTICLE_LINK = 'https://manychat.com/blog/manychat-pricing-is-changing/'

export const PostMigrationToNewBillingModal = ({
  onNext,
  onSeeAllPlansClick,
  ctaLabel,
}: UniversalPaywallContextFCProps) => {
  const activeContacts = useAppSelector(getActiveContacts)
  const connectedChannels = useAppSelector(getConnectedChannels)
  const modalImage =
    activeContacts > TouchedContactLimits.free ? modalImageStopped : modalImageDefault

  const handleFindOutMoreClick = () => {
    analyticsService.sendEvent(
      'POST_MIGRATION_FREE_USERS_TO_NEW_BILLING.MODAL.FIND_OUT_MORE.CLICKED',
    )
  }

  return (
    <div className={cm.container}>
      <div className={cm.content}>
        <div>
          <h1 className="text-display-lg m-b">{l.translate('Welcome to your new plan')}</h1>
          <p className="text-subheading">
            <span>
              {l.translate(
                'The big difference across all plans: contacts are now based on people who engage with you each month, not every contact in your list.',
              )}
            </span>
            <Link
              variant="subheading"
              href={ARTICLE_LINK}
              target="_blank"
              rel="noreferrer"
              className="m-l-xxs d-inline"
              onClick={handleFindOutMoreClick}
            >
              {l.translate('Find out more')}
            </Link>
          </p>
          <h3 className="text-heading-sm m-t-md">
            {l.translate('What changed on your Free plan:')}
          </h3>
          <ul className="m-t-md m-b-lg p-l-0 d-flex flex-col gap">
            <li>
              <div className="d-inline-flex align-center text-subheading m-b-xs">
                <span>{l.translate('Total Contacts → Active Contacts')}</span>
                <Icon.Question
                  size={16}
                  className="m-l-xxs"
                  data-title={l.translate(
                    'People you interact with during the billing period via automations or DMs, no matter how any messages are exchanged. Every plan includes a monthly Active Contact limit that you can manage freely.',
                  )}
                  aria-hidden="true"
                />
              </div>
              <div className="text-body">
                <ActiveContactsText activeContacts={activeContacts} />
              </div>
            </li>
            <li>
              <div className="text-subheading m-b-xs">{l.translate('3 channels → 2 channels')}</div>
              <div className="text-body text-secondary">
                <ChannelsText connectedChannels={connectedChannels} />
              </div>
            </li>
          </ul>

          <InfoSectionText activeContacts={activeContacts} />
        </div>
        <div className="d-flex gap">
          <BtnV2 variant="primary" onClick={onNext}>
            {l.getString(ctaLabel)}
          </BtnV2>
          <BtnV2 variant="default" onClick={onSeeAllPlansClick}>
            {l.translate('See all plans')}
          </BtnV2>
        </div>
      </div>
      <div className={cm.imageContainer}>
        <img src={modalImage} alt={l.translate('Welcome to your new plan')} />
      </div>
    </div>
  )
}
